/* eslint-disable indent */
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import { Modal } from "components/atoms/Modal";
import Tabs from "components/atoms/Tab";
import { ADetailerModal } from "components/molecules/ADetailerModal";
import { BGRemovalModal } from "components/molecules/BGRemovalModal";
import CanvasModal from "components/molecules/CanvasModal";
import CommentSection from "components/molecules/CommentSection";
import ControlsContainer from "components/molecules/ControlsContainer";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import { EditModal } from "components/molecules/EditModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import { FaceSwapModal } from "components/molecules/FaceSwapModal";
import { InpaintingModal } from "components/molecules/InpaintingModal";
import ShareModal from "components/molecules/ShareModal";
import StatsSection from "components/molecules/StatsSection";
import { UpscalerModal } from "components/molecules/UpscalerModal";
import { deleteImage, getCommentsByImage, getMonsterStatsByImageId, getNPCStatsByImageId } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, GameEntityDTO, ICommentDTO, ImageDTO, RequestType, UserPremiumType } from "types";
import { calculateTotalCommentsSize } from "utils/utils";
import * as Styled from "./Lightbox.styled";
interface ImageLightboxWithMetadataProps {
  imageDtos: ImageDTO[];
  initialImageId?: string;
  onClose: () => void;
  open: boolean;
  onToggleVisibility: (imageId: string, newVisibility: boolean) => void;
  onVote: (imageId: string) => void;
  onIndexChange: (newIndex: number) => void;
}
export const LightboxWithMetadata: FC<ImageLightboxWithMetadataProps> = ({
  imageDtos,
  initialImageId = "",
  onClose,
  open,
  onToggleVisibility,
  onVote,
  onIndexChange
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [modalOpen, setModalOpen] = useState<boolean>(open);
  const [images, setImages] = useState<ImageDTO[]>(imageDtos);
  const [currentIndex, setCurrentIndex] = useState(images.findIndex(dto => dto.id === initialImageId));
  const [imageInfo, setImageInfo] = useState(imageDtos[currentIndex] || null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [comments, setComments] = useState<ICommentDTO[]>([]);
  const [isADetailerModalOpen, setIsADetailerModalOpen] = useState(false);
  const [isFaceSwapModalOpen, setIsFaceSwapModalOpen] = useState(false);
  const [isBGRemovalModalOpen, setIsBGRemovalModalOpen] = useState(false);
  const [stats, setStats] = useState<GameEntityDTO | null>(null);
  const lastFetchedIdRef = useRef<string | null>(null);
  const [isCanvasModalOpen, setCanvasModalOpen] = useState<boolean>(false);
  const [isInpaintingModalOpen, setInpaintingModalOpen] = useState(false);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [isUpscalerModalOpen, setUpscalerModalOpen] = useState(false);
  const handleCloseUpscalerModal = () => {
    setUpscalerModalOpen(false);
  };
  const handleUpscalerModalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageInfo.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
      toast.error("This image has already been upscaled and cannot be upscaled again.");
    } else {
      setUpscalerModalOpen(true);
    }
  };
  const handleInpaintClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setCanvasModalOpen(true);
  };
  const handleCloseCanvasModal = () => {
    setCanvasModalOpen(false);
  };
  const handleMaskSubmit = (maskData: string) => {
    setBase64Image(maskData); // Store the base64 data from the canvas
    setCanvasModalOpen(false); // Close the canvas modal
    setInpaintingModalOpen(true); // Open the inpainting modal
  };
  const handleCloseInpaintingModal = () => {
    setInpaintingModalOpen(false);
    setBase64Image(null); // Clear the image data
  };
  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShareModalOpen(true);
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const handleCloseADetailerModal = () => {
    setIsADetailerModalOpen(false);
  };
  const handleCloseFaceSwapModal = () => {
    setIsFaceSwapModalOpen(false);
  };
  const handleCompetitionsClick = () => {
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleEditClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setShowEditModal(true);
  };
  const handleCloseBGRemovalModal = () => {
    setIsBGRemovalModalOpen(false);
  };
  const handleBGRemovalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setIsBGRemovalModalOpen(true);
  };
  const handleADetailerClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setIsADetailerModalOpen(true);
  };
  const handleFaceSwapClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setIsFaceSwapModalOpen(true);
  };
  const handleEditCancel = () => {
    setShowEditModal(false);
  };
  const moveToNextImage = useCallback(() => {
    if (!isShareModalOpen) {
      setCurrentIndex(prevIndex => {
        const newIndex = (prevIndex + 1) % imageDtos.length;
        onIndexChange(newIndex);
        return newIndex;
      });
    }
  }, [imageDtos.length, onIndexChange, isShareModalOpen]);
  const moveToPreviousImage = useCallback(() => {
    if (!isShareModalOpen) {
      setCurrentIndex(prevIndex => {
        const newIndex = (prevIndex - 1 + imageDtos.length) % imageDtos.length;
        onIndexChange(newIndex); // Notify the parent component
        return newIndex;
      });
    }
  }, [imageDtos.length, onIndexChange, isShareModalOpen]);
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  useEffect(() => {
    const newIndex = imageDtos.findIndex(dto => dto.id === initialImageId);
    setCurrentIndex(newIndex);
    setImageInfo(imageDtos[newIndex] || null);
  }, [imageDtos, initialImageId]);
  useEffect(() => {
    setImageInfo(images[currentIndex]);
  }, [currentIndex, images]);
  useEffect(() => {
    setModalOpen(open);
  }, [open]);
  useEffect(() => {
    if (imageDtos) {
      const mergedImages = imageDtos.map(dto => ({
        ...dto,
        isVotedByUser: images.find(img => img.id === dto.id)?.isVotedByUser ?? dto.isVotedByUser,
        voteCount: images.find(img => img.id === dto.id)?.voteCount ?? dto.voteCount
      }));
      setImages(mergedImages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageDtos]);
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.isVotedByUser;
    try {
      await onVote(imageId);
      const updatedImages = images.map(img => {
        if (img.id === imageId) {
          return {
            ...img,
            isVotedByUser: newVoteStatus,
            voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
          };
        }
        return img;
      });
      setImages(updatedImages);
      if (imageInfo.id === imageId) {
        setImageInfo(prev => ({
          ...prev,
          isVotedByUser: newVoteStatus,
          voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
        }));
      }
      const newIndex = updatedImages.findIndex(img => img.id === imageId);
      setCurrentIndex(newIndex);
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await deleteImage(imageToDelete);
      const currentIndex = images.findIndex(img => img.id === imageToDelete);
      const newImageDtos = images.filter(img => img.id !== imageToDelete);
      setImages(newImageDtos);
      if (newImageDtos.length > 0) {
        let newIndex = currentIndex;
        if (currentIndex >= newImageDtos.length) {
          newIndex = newImageDtos.length - 1;
        }
        setImageInfo(newImageDtos[newIndex]);
      } else {
        onClose();
      }
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const [toggles, setToggles] = useState<{
    [key: string]: boolean;
  }>(imageDtos.reduce((acc, image) => ({
    ...acc,
    [image.id]: image.isPublic
  }), {}));
  useEffect(() => {
    setToggles(imageDtos.reduce((acc, image) => ({
      ...acc,
      [image.id]: image.isPublic
    }), {}));
  }, [imageDtos]);
  const handleToggle = async (imageId: string) => {
    const currentVisibility = toggles[imageId];
    const newVisibility = !currentVisibility;
    try {
      await onToggleVisibility(imageId, newVisibility);
      setToggles(prev => ({
        ...prev,
        [imageId]: newVisibility
      }));
    } catch (error: any) {
      captureException("Failed to update visibility status.", error);
    }
  };
  useEffect(() => {
    if (imageInfo) {
      const fetchComments = async () => {
        const response = await getCommentsByImage(imageInfo.id);
        if (response.comments) {
          setComments(response.comments);
        } else if (response.error) {
          console.error("Failed to fetch comments:", response.error);
        }
      };
      fetchComments();
    }
  }, [imageInfo]);
  useEffect(() => {
    let isMounted = true; // Flag to track if the component is mounted

    const fetchStats = async () => {
      if (!imageInfo) return;
      if (imageInfo.id !== lastFetchedIdRef.current) {
        let response;
        switch (imageInfo.imageMetadata.requestType) {
          case RequestType.MONSTER:
            response = await getMonsterStatsByImageId(imageInfo.id);
            break;
          case RequestType.NPC:
            response = await getNPCStatsByImageId(imageInfo.id);
            break;
          default:
            return;
        }
        if (!response) {
          if (isMounted) setStats(null);
        } else {
          if (isMounted) setStats(response);
        }
        lastFetchedIdRef.current = imageInfo.id;
      }
    };
    fetchStats();
    return () => {
      isMounted = false;
    };
  }, [imageInfo]);
  const formatRequestType = (requestType: string) => {
    if (!requestType) return "";
    return requestType.charAt(0).toUpperCase() + requestType.slice(1).toLowerCase();
  };
  const renderMetadata = () => {
    if (!imageInfo) return null;
    return <Styled.MetaDataSection data-sentry-element="unknown" data-sentry-component="renderMetadata" data-sentry-source-file="index.tsx">
                {imageInfo.imageMetadata && <Styled.DetailsContainer>
                        <Styled.Detail>
                            <Styled.DetailLabel>Username:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.username}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.Detail>
                            <Styled.DetailLabel>Created At:</Styled.DetailLabel>
                            <Styled.DetailValue suppressHydrationWarning>
                                {new Date(imageInfo.createdAt).toLocaleString()}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.HorizontalRule />
                        <Styled.Detail>
                            <Styled.DetailLabel>Image Type:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {formatRequestType(imageInfo.imageMetadata.requestType)}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.Detail>
                            <Styled.DetailLabel>Theme:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.theme}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.Detail>
                            <Styled.DetailLabel>Premium:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.premium.toString()}
                            </Styled.DetailValue>
                        </Styled.Detail>
                    </Styled.DetailsContainer>}
                <Styled.DetailsContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {Object.entries(imageInfo.imageMetadata.npcFieldsMap).map(([key, value]) => value && <Styled.Detail key={key}>
                                    <Styled.DetailLabel>
                                        {key}:{" "}
                                    </Styled.DetailLabel>
                                    <Styled.DetailValue>
                                        {value}
                                    </Styled.DetailValue>
                                </Styled.Detail>)}
                </Styled.DetailsContainer>
                <Styled.HorizontalRule data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                {imageInfo.imageMetadata.description && <Styled.DetailsContainer>
                        <Styled.Detail>
                            <Styled.DetailLabel>
                                Description:
                            </Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.description}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.HorizontalRule />
                    </Styled.DetailsContainer>}
                {imageInfo.imageMetadata.modelData && <Styled.DetailsContainer>
                        <Styled.Detail>
                            <Styled.DetailLabel>Model:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.modelData.model}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.Detail>
                            <Styled.DetailLabel>CFG Scale:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.modelData.cfg}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.Detail>
                            <Styled.DetailLabel>Steps:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.modelData.steps}
                            </Styled.DetailValue>
                        </Styled.Detail>
                        <Styled.Detail>
                            <Styled.DetailLabel>Sampler:</Styled.DetailLabel>
                            <Styled.DetailValue>
                                {imageInfo.imageMetadata.modelData.sampler}
                            </Styled.DetailValue>
                        </Styled.Detail>
                    </Styled.DetailsContainer>}
                <ControlsContainer imageInfo={imageInfo} toggles={toggles} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClick} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} data-sentry-element="ControlsContainer" data-sentry-source-file="index.tsx" />
            </Styled.MetaDataSection>;
  };
  const renderComments = () => <CommentSection image={imageInfo} initialComments={comments} data-sentry-element="CommentSection" data-sentry-component="renderComments" data-sentry-source-file="index.tsx" />;
  const renderStats = useCallback(() => {
    return <StatsSection image={imageInfo} statsProps={stats} />;
  }, [imageInfo, stats]);
  const tabs = [{
    label: "Metadata",
    content: renderMetadata()
  }, {
    label: "Stats",
    content: renderStats()
  }, {
    label: `Comments (${calculateTotalCommentsSize(comments)})`,
    content: renderComments()
  }];
  return <Modal open={modalOpen} handleBackdropClick={onClose} controls moveBack={moveToPreviousImage} moveForward={moveToNextImage} data-sentry-element="Modal" data-sentry-component="LightboxWithMetadata" data-sentry-source-file="index.tsx">
            <Styled.StyledModal data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.ImageContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.StyledImage src={imageInfo?.image} alt="Artwork" data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Styled.ImageContainer>
                <Tabs tabs={tabs} data-sentry-element="Tabs" data-sentry-source-file="index.tsx" />
            </Styled.StyledModal>
            <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={imageInfo?.id} data-sentry-element="EligibleCompetitionsModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            {imageInfo && <EditModal isOpen={showEditModal} onCancel={handleEditCancel} imageId={imageInfo.id} />}
            <ShareModal isOpen={isShareModalOpen} image={images.find(img => img.id === imageInfo?.id) || null} onClose={handleCloseShareModal} data-sentry-element="ShareModal" data-sentry-source-file="index.tsx" />
            {imageInfo && <ADetailerModal isOpen={isADetailerModalOpen} onClose={handleCloseADetailerModal} imageId={imageInfo.id} isPremium={imageInfo?.imageMetadata.premium || false} />}
            {imageInfo && <FaceSwapModal isOpen={isFaceSwapModalOpen} onClose={handleCloseFaceSwapModal} imageId={imageInfo.id} />}
            {imageInfo && <BGRemovalModal isOpen={isBGRemovalModalOpen} onClose={handleCloseBGRemovalModal} imageId={imageInfo.id} />}
            {imageInfo && <CanvasModal isOpen={isCanvasModalOpen} onClose={handleCloseCanvasModal} imageDto={imageInfo} onMaskSubmit={handleMaskSubmit} />}
            {imageInfo && <InpaintingModal isOpen={isInpaintingModalOpen} onCancel={handleCloseInpaintingModal} base64Image={base64Image!} originalImage={imageInfo.image} imageId={imageInfo.id} handleInpaintClick={handleInpaintClick} />}
            {imageInfo && <UpscalerModal isOpen={isUpscalerModalOpen} onClose={handleCloseUpscalerModal} imageId={imageInfo.id} />}
        </Modal>;
};